/**
 * Shared 12-hour time picker (hour 1–12, minute 00/30, AM/PM).
 * All scheduling logic uses 24-hour format internally; this component
 * only handles display/input in the 12-hour convention.
 *
 * Props:
 *   hour        {number}   1–12
 *   minute      {number}   0 or 30
 *   period      {string}   'AM' | 'PM'
 *   onChange    {fn}       called with { hour, minute, period }
 *   testIdPrefix {string}  prefix for data-testid attributes (default: 'time')
 */
const TimePicker = ({ hour, minute, period, onChange, testIdPrefix = 'time' }) => {
  const update = (field, value) => onChange({ hour, minute, period, [field]: value });

  return (
    <div className="flex items-center gap-2">
      {/* Hour 1–12 */}
      <select
        value={hour}
        data-testid={`${testIdPrefix}-hour-select`}
        onChange={(e) => update('hour', Number(e.target.value))}
        className="flex-1 bg-slate-50 border border-transparent focus:border-slate-300 rounded-xl px-2 py-3 text-slate-900 text-center font-semibold text-sm"
      >
        {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>

      <span className="text-slate-400 font-bold text-xl select-none">:</span>

      {/* Minute: 00 or 30 only */}
      <select
        value={minute}
        data-testid={`${testIdPrefix}-minute-select`}
        onChange={(e) => update('minute', Number(e.target.value))}
        className="flex-1 bg-slate-50 border border-transparent focus:border-slate-300 rounded-xl px-2 py-3 text-slate-900 text-center font-semibold text-sm"
      >
        <option value={0}>00</option>
        <option value={30}>30</option>
      </select>

      {/* AM / PM */}
      <div className="flex rounded-xl overflow-hidden border border-slate-200 flex-shrink-0">
        <button
          type="button"
          data-testid={`${testIdPrefix}-period-am`}
          onClick={() => update('period', 'AM')}
          className={`px-4 py-3 text-sm font-semibold transition-colors ${period === 'AM' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500'}`}
        >
          AM
        </button>
        <button
          type="button"
          data-testid={`${testIdPrefix}-period-pm`}
          onClick={() => update('period', 'PM')}
          className={`px-4 py-3 text-sm font-semibold transition-colors ${period === 'PM' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500'}`}
        >
          PM
        </button>
      </div>
    </div>
  );
};

export default TimePicker;
